import { Button, Input, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { trackButtonClick } from '../../analytics/mixpanel';
import { TriangleExclamation } from '../../assets';
import MagicIconSVG from '../../assets/MagicIconSVG';
import InputUploadDocChat from '../../CommonComponents/InputUpload/InputUploadDocChat';
import {
  ACTIVE_TAB_MAPPING,
  DOCCHAT_ACTIVE_TAB_KEY,
  ERROR,
  SUCCESS,
} from '../../Constants/commonConstants';
import { getChatHistory } from '../../Redux/DocChat/docChatActions';
import { useLocalStorage } from '../../utils/hooks/useLocalStorage/useLocalStorage';
import { useTakeTour } from '../TakeTour/hooks/useTakeTour';
import CreateFolderModal from './CreateFolderModal';
import FileUploadTips from './FileUploadTips';
import SelectedOptionContainer from './SelectedOptionContainer';

function FilePickerDocChat({ docChatQuota, sendData, addFileData }) {
  const dispatch = useDispatch();
  const { uploadFile, uploadStatus } = useSelector((state) => state.docChatData);
  const { message, documentId, collection_id, documents, presetId } = uploadFile;
  const navigate = useNavigate();
  const [, setActiveTab] = useLocalStorage(DOCCHAT_ACTIVE_TAB_KEY, ACTIVE_TAB_MAPPING.chat);
  const [fileStatus, setFileStatus] = useState(false);
  const [uploadData, setUploadData] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptionContainerOpen, setSelectedOptionContainerOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState({});
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const { userData } = useSelector((state) => state.loginState);
  const [askChatQuery, setAskChatQuery] = useState(null);

  const { dropdownAndMiscStates } = useTakeTour();

  const calculatedSelectedOption =
    dropdownAndMiscStates.filePickerDocChatSelectedOption === 'Your Workflows'
      ? 'Your Workflows'
      : selectedOption;
  const calculatedSelectedOptionContainerOpen =
    dropdownAndMiscStates.filePickerDocChatSelectedOptionContainerOpen ||
    selectedOptionContainerOpen;

  useEffect(() => {
    if (uploadStatus === SUCCESS) {
      setData();
    }
  }, [uploadStatus]);

  const setData = async () => {
    await dispatch(getChatHistory({ userId: userData?.id }));

    // Save tab to localStorage instead of URL
    setActiveTab(presetId ? ACTIVE_TAB_MAPPING.presets : ACTIVE_TAB_MAPPING.chat);

    if (collection_id || addFileData?.collection_id) {
      navigate({
        pathname: '/viewer-docchat',
        search: `?uuid=${collection_id || addFileData?.collection_id}&type=SAVED&position=${0}`,
      });
    } else {
      navigate({
        pathname: '/viewer-docchat',
        search: `?uuid=${documentId || documents?.[0]?.documentID}&type=SAVED`,
      });
    }
  };

  const openModal = () => {
    setIsCreateFolderOpen(true);
  };

  const closeModal = () => {
    setIsCreateFolderOpen(false);
  };

  const handleCreateFolder = (value) => {
    trackButtonClick({
      type: `Document Upload Docchat via create folder - ${calculatedSelectedOption} - ${selectedAction?.label?.props?.preset_name}`,
    });
    sendData({
      file: uploadData,
      uploadType: 'collection',
      collectionName: value,
      query: askChatQuery,
      presetId: selectedAction?.key,
    });
  };

  const handleOption = (value) => {
    trackButtonClick({ type: `DocChat - Dashboard option selected - ${value}` });
    if (askChatQuery) {
      setAskChatQuery(null);
    }
    setSelectedOptionContainerOpen(true);
    setSelectedAction({});
    setSelectedOption(value);
  };

  return (
    <div className='file-picker-docchat'>
      <div className='file-picker-header'>
        <h1
          className='text-gradient text-center'
          style={{ fontSize: '24px', wordBreak: 'break-all' }}
        >
          {`Hello, ${userData?.name}`}
        </h1>
        <p className='mb-24' style={{ color: 'var(--grey-9)', marginTop: '12px' }}>
          Need help with reviewing, analysing risks or workflows documents?
        </p>
      </div>
      <section className='upload-section'>
        <div className='upload-section-inner'>
          <div
            id='upload-docchat'
            className={`upload-docchat ${fileStatus && uploadData?.length > 0 ? 'bg' : ''}`}
          >
            <div className='upload-inner'>
              {docChatQuota === 0 ? (
                <div className='exhausted-quota'>
                  <TriangleExclamation />
                  <h4>You have exhausted your quota.</h4>
                  <p>
                    Please reach out to{' '}
                    <a className='troubleshoot-link' href='mailto:team@mikelegal.com'>
                      team@mikelegal.com
                    </a>{' '}
                    to increase your quota.
                  </p>
                </div>
              ) : (
                <InputUploadDocChat
                  fileStatus={fileStatus}
                  setFileStatus={setFileStatus}
                  uploadData={uploadData}
                  setUploadData={setUploadData}
                  product='DOC_CHAT'
                />
              )}
            </div>
          </div>
          {calculatedSelectedOption ? (
            <SelectedOptionContainer
              selectedOptionContainerOpen={calculatedSelectedOptionContainerOpen}
              setSelectedOptionContainerOpen={setSelectedOptionContainerOpen}
              selectedOption={calculatedSelectedOption}
              onClose={() => {
                setSelectedOption(null);
                setSelectedAction({});
              }}
              selectedAction={selectedAction}
              setSelectedAction={setSelectedAction}
            />
          ) : (
            <Input
              placeholder='Ask DocChat'
              style={{ height: '54px', borderRadius: '16px' }}
              id='ask-docchat-input'
              className='mb-24'
              value={askChatQuery}
              onChange={(e) => setAskChatQuery(e.target.value)}
            />
          )}
          <div className='flex' style={{ gap: '1rem', justifyContent: 'center' }}>
            <Tooltip
              placement='bottom'
              title='Click and select an action from the dropdown to run our ready-made preset prompts.'
            >
              <button
                id='dashboard-mikelegal-magic-button'
                className='option-btn gradient'
                onClick={() => handleOption("MikeLegal's Magic")}
                disabled={calculatedSelectedOption === "MikeLegal's Magic"}
                type='button'
              >
                <span className='text-gradient'>MikeLegal's Magic</span>
                <MagicIconSVG size={16} />
              </button>
            </Tooltip>
            {/* <button
              className="option-btn"
              onClick={() => handleOption('Risk Assessment')}
              disabled={selectedOption === 'Risk Assessment'}
            >
              Run Risk Assessment Analysis
            </button> */}
            <Tooltip
              placement='bottom'
              title='Click and choose a workflow from the dropdown to run your custom extraction prompt.'
            >
              <button
                id='dashboard-your-workflows-button'
                className='option-btn'
                onClick={() => handleOption('Your Workflows')}
                disabled={calculatedSelectedOption === 'Your Workflows'}
                type='button'
              >
                Your Workflows
              </button>
            </Tooltip>
          </div>
          {fileStatus && uploadData?.length > 0 && (
            <div className='flex mt-24' style={{ gap: '1.5rem', justifyContent: 'center' }}>
              {uploadData.length === 1 ? (
                <Button
                  type='primary'
                  className='btn-l'
                  onClick={() => {
                    trackButtonClick({
                      type: `Single Document Upload Docchat - ${calculatedSelectedOption} - ${selectedAction?.label?.props?.preset_name}`,
                    });
                    sendData({
                      file: uploadData,
                      query: askChatQuery,
                      presetId: selectedAction?.key,
                    });
                  }}
                >
                  Start DocChat
                </Button>
              ) : (
                <>
                  <Button
                    color='primary'
                    variant='outlined'
                    className='btn-l'
                    onClick={() => {
                      trackButtonClick({
                        type: `Document Upload Docchat Separately - ${calculatedSelectedOption} - ${selectedAction?.label?.props?.preset_name}`,
                      });
                      sendData({
                        file: uploadData,
                        uploadType: 'individual',
                        query: askChatQuery,
                        presetId: selectedAction?.key,
                      });
                    }}
                  >
                    Upload Separately and Start
                  </Button>
                  <Button type='primary' className='btn-l' onClick={openModal}>
                    Create a Folder and Start
                  </Button>
                </>
              )}
            </div>
          )}
          {fileStatus && uploadData?.length > 0 && uploadStatus === ERROR && message && (
            <p className='fs-14 mt-16 color-danger text-center'>{message}</p>
          )}
        </div>
      </section>
      <FileUploadTips />
      <CreateFolderModal
        isOpen={isCreateFolderOpen}
        onClose={closeModal}
        onSave={handleCreateFolder}
      />
    </div>
  );
}

export default FilePickerDocChat;
